import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Clock, BookOpen, ArrowRight, Sparkles } from "lucide-react";
import { GUIDES } from "@/data/guides";
import { LiveMapButton } from "@/components/LiveMapButton";

const INSIGHT_CATEGORIES = [
  {
    name: "Asset Intelligence",
    blurb: "Airspace, infrastructure and property — what the law permits around the assets that matter.",
    slugs: [
      "can-i-fly-a-drone-near-critical-national-infrastructure-uk",
      "can-i-fly-a-drone-near-an-airport-uk",
      "can-i-fly-a-drone-near-railways-uk",
      "can-i-fly-a-drone-over-roads-uk",
      "can-i-fly-a-drone-over-private-property-uk",
      "drone-insurance-uk-explained",
    ],
  },
  {
    name: "Construction Intelligence",
    blurb: "Registration, compliance and enforcement — the operational framework behind every lawful flight.",
    slugs: [
      "uk-drone-laws",
      "drone-registration-uk-flyer-id-operator-id",
      "remote-id-uk-explained",
      "drone-fines-penalties-uk",
      "commercial-drone-work-uk",
      "a2-cofc-worth-it",
    ],
  },
  {
    name: "Survey Intelligence",
    blurb: "Site assessment done properly — how to read airspace, terrain and conditions before the props spin.",
    slugs: [
      "where-can-i-fly-my-drone-uk",
      "how-to-check-if-you-can-fly-a-drone-anywhere-uk",
      "can-i-fly-a-drone-in-national-parks-uk",
      "can-i-fly-a-drone-over-a-beach-uk",
      "can-i-fly-a-drone-in-my-garden-uk",
      "flying-a-drone-at-night-uk",
    ],
  },
  {
    name: "Commercial Media",
    blurb: "Cameras, aircraft and the people in frame — capturing and publishing aerial footage lawfully.",
    slugs: [
      "uk-drone-photography-privacy-laws",
      "dji-mini-sub-250g-rules-uk",
      "dji-air-series-drone-rules-uk",
      "dji-mavic-series-drone-rules-uk",
      "drone-laws-for-under-18s-uk",
    ],
  },
];

export const GuideCard = ({ guide, compact = false, featured = false }) => (
  <Link
    to={`/guides/${guide.slug}`}
    data-testid="guide-card"
    className="group bg-white/[0.03] border border-white/10 rounded-3xl overflow-hidden hover:border-[#2ec4b6]/50 hover:bg-white/[0.06] transition-colors flex flex-col"
  >
    <div className={`relative overflow-hidden ${compact ? "h-36" : "h-48"}`}>
      <img src={guide.hero} alt={guide.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
      {featured ? (
        <span data-testid="featured-insight-badge" className="absolute top-3 left-3 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.15em] px-2.5 py-1 rounded-full bg-[#2ec4b6] text-black">
          <Sparkles size={10} /> Featured Insight
        </span>
      ) : (
        <span className="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-[0.15em] px-2.5 py-1 rounded-full bg-black/70 backdrop-blur text-[#2ec4b6] border border-[#2ec4b6]/30">
          {guide.category}
        </span>
      )}
    </div>
    <div className="p-5 flex flex-col flex-1">
      <h3 className="font-heading font-bold text-lg text-white leading-snug group-hover:text-[#2ec4b6] transition-colors">{guide.title}</h3>
      <p className="text-sm text-zinc-300 mt-2 leading-relaxed line-clamp-2 flex-1">{guide.description}</p>
      <div className="flex items-center justify-between mt-4">
        <div className="flex items-center gap-3 text-xs text-zinc-400">
          <span className="inline-flex items-center gap-1"><Clock size={12} /> {guide.readTime} min read</span>
          <span className="hidden sm:inline">Updated {guide.updated}</span>
        </div>
        <span data-testid="read-insight-button" className="inline-flex items-center gap-1 text-xs font-bold text-[#2ec4b6] group-hover:gap-2 transition-all">
          Read Insight <ArrowRight size={12} />
        </span>
      </div>
    </div>
  </Link>
);

const CategorySection = ({ category }) => {
  const guides = category.slugs.map((s) => GUIDES.find((g) => g.slug === s)).filter(Boolean);
  if (guides.length === 0) return null;
  return (
    <section data-testid={`insight-category-${category.name.toLowerCase().replace(/\s+/g, "-")}`} className="mt-16">
      <div className="flex items-baseline gap-4 border-b border-white/10 pb-4">
        <h2 className="font-heading text-lg font-bold text-white tracking-tight">{category.name}</h2>
        <p className="text-sm text-zinc-400 hidden md:block">{category.blurb}</p>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
        {guides.map((g, i) => (
          <GuideCard key={g.slug} guide={g} featured={i === 0} />
        ))}
      </div>
    </section>
  );
};

export default function GuidesIndex() {
  useEffect(() => {
    document.title = "Insights — Aerial Intelligence, Explained | wherecaniflymydrone";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="guides-page" className="min-h-screen bg-[#0a0a0a] text-zinc-100">
      <div className="max-w-6xl mx-auto px-6 pt-10 pb-20">
        <div className="flex items-center justify-between mb-8">
          <Link to="/" data-testid="guides-back-home" className="inline-flex items-center gap-1.5 text-xs text-zinc-300 hover:text-[#2ec4b6] transition-colors">
            <ArrowLeft size={14} /> Back to home
          </Link>
          <LiveMapButton testId="guides-open-map" size="sm" />
        </div>
        <div className="flex items-center gap-3 mb-2">
          <BookOpen size={20} className="text-[#2ec4b6]" />
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#2ec4b6]">Insights</p>
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight">Aerial intelligence, explained.</h1>
        <p className="text-base text-zinc-300 mt-4 max-w-2xl leading-relaxed">
          Authoritative, UK-specific analysis of drone regulation, airspace and operations — researched against CAP 722 and the Air Navigation Order, written for the pilots and organisations who fly for real outcomes, and kept current as the rules evolve.
        </p>
        {INSIGHT_CATEGORIES.map((c) => (
          <CategorySection key={c.name} category={c} />
        ))}
      </div>
    </div>
  );
}
